import React, { useEffect, useState, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";
import ManagerSidebar from "../../../components/Sidebar/ManagerSidebar";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaTimes,
  FaCloudUploadAlt,
  FaTag,
  FaDollarSign,
  FaBoxes,
  FaBars,
} from "react-icons/fa";

import "./InventoryManagement.css";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const BASE_URL = "https://ikaze-thouse-backend.vercel.app";

export default function InventoryManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // AVAILABLE UNITS
  const [allApartmentUnits, setAllApartmentUnits] = useState([]);

  const [form, setForm] = useState({
    description: "",
    category: "Rent",
    price: "",
    unitname: "",
  });

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  // ================= FETCH ITEMS =================
  const fetchItems = useCallback(async () => {
    try {
      setFetching(true);

      const res = await fetch(`${BASE_URL}/api/items`);
      const data = await res.json();

      setItems(data);

    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setFetching(false);
    }
  }, []);

  // ================= FETCH AVAILABLE UNITS =================
  const fetchAllApartmentUnits = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/v1/appartmentUnits/getAll`
      );

      const data = await res.json();

      if (data.success) {
        setAllApartmentUnits(
          data.data.filter(
            (unit) => unit.status === "Available"
          )
        );
      }

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchAllApartmentUnits();
  }, [fetchItems]);

  // ================= FILE CHANGE =================
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  // ================= UPLOAD IMAGE =================
  const uploadImage = async (file) => {
    const fileName = `item-${Date.now()}-${file.name.replace(/\s/g, "_")}`;

    const { error } = await supabase.storage
      .from("items")
      .upload(fileName, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from("items")
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  // ================= CREATE / UPDATE =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      let imageUrl = "";

      // KEEP OLD IMAGE DURING EDIT
      if (editingId) {
        const existing = items.find(
          (item) => item.id === editingId
        );

        imageUrl = existing?.image_url || "";
      }

      // UPLOAD NEW IMAGE
      if (file) {
        imageUrl = await uploadImage(file);
      }

      const payload = {
        description: form.description,
        category: form.category,
        price: form.price,
        unitname: form.unitname,
        image_url: imageUrl,
      };

      const url = editingId
        ? `${BASE_URL}/api/items/${editingId}`
        : `${BASE_URL}/api/items`;

      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Request failed");
      }

      await fetchItems();

      resetForm();

    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ================= RESET FORM =================
  const resetForm = () => {
    setEditingId(null);

    setForm({
      description: "",
      category: "Rent",
      price: "",
      unitname: "",
    });

    setFile(null);
    setPreview(null);
    setShowForm(false);
  };

  // ================= EDIT =================
  const startEdit = (item) => {
    setEditingId(item.id);

    setForm({
      description: item.description,
      category: item.category,
      price: item.price,
      unitname: item.unitname || "",
    });

    setPreview(item.image_url);

    setShowForm(true);
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!id) {
      alert("Invalid ID");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this item?"
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(
        `${BASE_URL}/api/items/${id}`,
        {
          method: "DELETE",
        }
      );

      const data = await res.json();

      if (res.ok) {
        setItems((prev) =>
          prev.filter(
            (item) => String(item.id) !== String(id)
          )
        );
      } else {
        alert(data.error || "Delete failed");
      }

    } catch (err) {
      console.error(err);
      alert("Server error while deleting");
    }
  };

  // ================= UI =================
  return (
    <div className={`admin-layout ${sidebarOpen ? "sidebar-active" : ""}`}>
      
      <div className="sidebar-wrapper">
        <ManagerSidebar />
      </div>

      <main className="inventory-main">

        {/* MOBILE NAV */}
        <div className="mobile-nav">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="menu-toggle"
          >
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>

          <span className="mobile-logo">
            IkazeHouse
          </span>
        </div>

        {/* HEADER */}
        <header className="inventory-header">

          <div className="title-area">
            <h1>Inventory Dashboard</h1>

            <p>
              Track and manage your property listings efficiently.
            </p>
          </div>

          <button
            className="btn-add"
            onClick={() => setShowForm(true)}
          >
            <FaPlus />
            <span>Add Listing</span>
          </button>

        </header>

        {/* FORM MODAL */}
        {showForm && (
          <div className="modal-overlay">

            <section className="modal-card animate-in">

              <div className="modal-header">

                <h3>
                  {editingId
                    ? "Update Property"
                    : "Create Listing"}
                </h3>

                <button
                  className="close-btn"
                  onClick={resetForm}
                >
                  <FaTimes />
                </button>

              </div>

              <form
                onSubmit={handleSubmit}
                className="modern-form"
              >

                <div className="form-content">

                  {/* IMAGE */}
                  <div className="upload-wrapper">

                    <label className="image-dropzone">

                      <input
                        type="file"
                        hidden
                        onChange={handleFileChange}
                      />

                      {preview ? (
                        <img
                          src={preview}
                          alt="preview"
                          className="img-preview"
                        />
                      ) : (
                        <div className="upload-placeholder">
                          <FaCloudUploadAlt size={32} />
                          <p>Click to upload image</p>
                        </div>
                      )}

                    </label>
                  </div>

                  {/* FIELDS */}
                  <div className="fields-wrapper">

                    {/* DESCRIPTION */}
                    <div className="input-field full-width">

                      <label>
                        <FaTag /> Description
                      </label>

                      <input
                        type="text"
                        value={form.description}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            description: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div className="row">

                      {/* CATEGORY */}
                      <div className="input-field">

                        <label>
                          <FaBoxes /> Type
                        </label>

                        <select
                          value={form.category}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              category: e.target.value,
                            })
                          }
                        >
                          <option value="Rent">
                            Rent
                          </option>

                          <option value="Sale">
                            Sale
                          </option>

                        </select>
                      </div>

                      {/* UNIT NAME */}
                      <div className="input-field">

                        <label>
                          <FaBoxes /> Unit Name
                        </label>

                        <select
                          value={form.unitname}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              unitname: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">
                            Select Unit
                          </option>

                          {allApartmentUnits.map((unit) => (
                            <option
                              key={unit.UnitId}
                              value={unit.unitname}
                            >
                              {unit.unitname}
                            </option>
                          ))}

                        </select>
                      </div>

                      {/* PRICE */}
                      <div className="input-field">

                        <label>
                          <FaDollarSign /> Price
                        </label>

                        <input
                          type="number"
                          value={form.price}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              price: e.target.value,
                            })
                          }
                          required
                        />
                      </div>

                    </div>
                  </div>
                </div>

                {/* FOOTER */}
                <div className="form-footer">

                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : editingId
                      ? "Update"
                      : "Create"}
                  </button>

                </div>
              </form>
            </section>
          </div>
        )}

        {/* ITEMS GRID */}
        {fetching ? (
          <p>Loading...</p>
        ) : (
          <div className="inventory-grid">

            {items.map((item) => (
              <article
                key={item.id}
                className="item-card"
              >

                <div
                  className={`status-pill ${item.category.toLowerCase()}`}
                >
                  {item.category}
                </div>

                <div className="card-image">

                  <img
                    src={item.image_url}
                    alt={item.description}
                  />

                  <div className="card-actions">

                    <button
                      onClick={() => startEdit(item)}
                      className="btn-icon edit"
                    >
                      <FaEdit />
                    </button>

                    <button
                      onClick={() => handleDelete(item.id)}
                      className="btn-icon delete"
                    >
                      <FaTrash />
                    </button>

                  </div>
                </div>

                <div className="card-body">

                  <h3>
                    {item.description}
                  </h3>

                  <p className="unit-name">
                    Unit: {item.unitname}
                  </p>

                  <div className="price-info">
                    $
                    {" "}
                    {Number(item.price).toLocaleString()}
                  </div>

                </div>

              </article>
            ))}

          </div>
        )}
      </main>
    </div>
  );
}